import Vue from 'vue';
import Vuex from 'vuex';
import jwtDecode from 'jwt-decode';
import forEach from 'lodash/forEach';
import mapKeys from 'lodash/mapKeys';
import upperFirst from 'lodash/upperFirst';

import { loadBlueprint, resolveBlueprintDataRefs } from '@/utils/blueprint-loader';
import { getFileLoader } from '@/utils/file-loader';
import { fromStorage } from '@/utils/storage';

Vue.use(Vuex);

const getGameId = () => {
    try {
        const { game_id } = jwtDecode(process.env.VUE_APP_TOKEN);

        return game_id;
    } catch {
        return fromStorage('gameId');
    }
};

export default new Vuex.Store({
    strict: process.env.NODE_ENV !== 'production',
    state: {
        gameId: getGameId(),
        drawings: {},
        scopes: {},
        pages: {},
        themeSettings: {},
        social: {},
        layout: {},
        game: {},
        salesChannel: {},
        parentSalesChannel: {},
        clubName: '',
        loading: 0,
    },
    getters: {
        page: state => pagePath => {
            if (process.env.VUE_APP_EDITOR) return state?.editor?.blueprint?.pages?.[pagePath];

            return state.pages[pagePath];
        },
        themeSettings(state) {
            if (process.env.VUE_APP_EDITOR) {
                return state?.editor?.blueprint?.themeSettings;
            }

            return state.themeSettings;
        },
        homePage({ pages }) {
            return find(pages, { isHome: true });
        },
        errorPage({ pages }) {
            return find(pages, { is404: true });
        },
        parentToken(state) {
            if (process.env.VUE_APP_EDITOR) return state?.editor?.data?.parentSalesChannel?.token;
            return state.parentSalesChannel.token;
        },
        token(state) {
            if (process.env.VUE_APP_EDITOR) return state?.editor?.data?.salesChannel?.token;
            return state.salesChannel.token;
        },
        cacheTimestamp(state) {
            if (process.env.VUE_APP_EDITOR) return state?.editor?.data?.game?.cacheTimestamp;
            return state.game.cacheTimestamp;
        },
        gameId({ gameId }) {
            return gameId;
        },
        socialData({ parentSalesChannel, salesChannel }) {
            const { config } = parentSalesChannel.config ? parentSalesChannel : salesChannel;
            if (!config) return {};

            return config;
        },
    },
    mutations: {
        SET_DATA(state, payload) {
            state[payload.key] = payload.value;
        },
        SET_CLUB_NAME(state, { clubName }) {
            state.clubName = clubName;
        },
        LOADED(state) {
            state.loading += 1;
        },
    },
    actions: {
        async initialize({ commit, state: { clubName } }) {
            try {
                /*
                    Use the contents given by the manifest to inject sales channel data into the state.
                    Expected json files:
                    - drawings-*.json
                    - faqs-*.json
                    - game-*.json
                    - game-docs-*.json
                    - pages-*.json
                    - sales-channel-*.json
                    - social-settings-*.json
                    - sponsors-*.json
                    - theme-settings-*.json
                */

                const fileLoader = getFileLoader();
                let pathPrefix = '';
                let parentBlueprint = {};

                if (clubName) {
                    pathPrefix = `../club/${clubName}/data/`;
                    parentBlueprint = await loadBlueprint(fileLoader);
                    parentBlueprint = mapKeys(parentBlueprint, (value, key) => `parent${upperFirst(key)}`);
                    forEach(parentBlueprint, (value, key) => commit('SET_DATA', { key, value }));
                }

                const blueprint = await loadBlueprint(fileLoader, { pathPrefix });
                const data = { ...blueprint, ...parentBlueprint };
                const resolvedBlueprint = resolveBlueprintDataRefs(blueprint, data);

                forEach(resolvedBlueprint.blueprint, (value, key) => commit('SET_DATA', { key, value }));

                commit('LOADED');
            } catch (err) {
                console.log(err);
                throw Error('Error trying to load blueprint');
            }
        },
        setClubName({ state, commit, dispatch }, { clubName }) {
            commit('SET_CLUB_NAME', { clubName });

            if (state.loading > 0) {
                dispatch('initialize');
            }
        },
    },
    modules: {},
});
